import { canEditSection } from '@/lib/access';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useManualForm, FormShell, NumberField, MoneyField, TextAreaField, RepeatTable, ReadOnlyField } from './_shared';
import { Card } from '@/components/UI';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { formatCurrency } from '@/lib/format';
import { CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT = {
  summary: {
    total_dispatched_value: 0,
    total_freight_cost: 0,
    premium_freight_instances: 0,
    premium_freight_cost: 0,
    schedule_adherence_pct: 0,
    notes: '',
  },
  customer_wise: [],
};

const CW_COLS = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'schedule_value', label: 'Schedule Value (₹)', type: 'money', right: true },
  { key: 'dispatched_value', label: 'Dispatched Value (₹)', type: 'money', right: true },
  { key: 'adherence_pct', label: 'Adherence %', type: 'readonly', suffix: '%', right: true },
  { key: 'ex_works', label: 'Self-pickup', type: 'checkbox' },
  { key: 'remarks', label: 'Remarks' },
];

const CODE_TO_PARSER_UNIT = { U1: 'Unit-1', U3: 'Unit-3', SL: 'Sanber Labs' };

export default function BusinessSales() {
  const ctx = useManualForm('business_sales', DEFAULT);
  const { currentUnit, currentCycle } = useCycle();
  const canEdit = canEditSection(ctx.user, 'business_sales');
  const locked = ctx.currentCycle?.status === 'locked';
  const s = ctx.data.summary || DEFAULT.summary;
  const touchedRef = useRef({});
  const markTouched = (key) => {
    touchedRef.current = { ...touchedRef.current, [key]: true };
  };
  const set = (key, val) => {
    markTouched(key);
    ctx.setData({ ...ctx.data, summary: { ...s, [key]: val } });
  };

  const [source, setSource] = useState(null);
  const [autofillStatus, setAutofillStatus] = useState('idle');
  const [syncing, setSyncing] = useState(false);

  const unitName = currentUnit ? CODE_TO_PARSER_UNIT[currentUnit.code] : null;
  const period = currentCycle ? `${currentCycle.year}-${String(currentCycle.month).padStart(2, '0')}` : null;

  const sync = useCallback(async (force = false) => {
    if (!unitName || !period) return;
    setSyncing(true);
    setAutofillStatus('loading');
    try {
      const r = await api.get(`/supply-chain/schedule-vs-supply/cycle-autofill`, {
        params: { unit: unitName, period },
      });
      const d = r.data || {};
      if (!d.found) {
        setSource(null);
        setAutofillStatus('none');
        if (force) toast.info(`No Schedule vs Supply data for ${currentUnit?.name} / ${period}`);
        return;
      }
      const fromOps = d.source === 'ops_api';
      setSource({ filename: d.source_filename, uploaded_at: d.source_uploaded_at, ops: fromOps, pieces: d.ops_pieces || null, xls: d.xls_filename || null });
      setAutofillStatus('idle');

      const t = touchedRef.current;
      // Functional update: the saved-entry load and this autofill race, so never
      // write over whatever state is current with a stale closure copy.
      ctx.setData((prev) => {
        const ps = prev.summary || DEFAULT.summary;
        const newSummary = { ...ps };
        if (force || !t.total_dispatched_value) newSummary.total_dispatched_value = d.total_dispatched_value;
        // The KPI export has no customer rows, so the adherence % cannot be derived
        // from the table below — take the export's own month % instead.
        if (fromOps && (force || !t.schedule_adherence_pct)) newSummary.schedule_adherence_pct = d.schedule_adherence_pct;

        const prevRows = prev.customer_wise || [];
        let nextRows = prevRows;
        if (!prevRows.length || force) {
          const remarksBy = Object.fromEntries(prevRows.map((p) => [p.customer_name, p.remarks]));
          nextRows = (d.customer_rows || []).map((c) => ({
            customer_name: c.customer,
            schedule_value: c.schedule_value,
            dispatched_value: c.dispatched_value,
            adherence_pct: c.adherence_pct,
            ex_works: !!c.ex_works,
            remarks: remarksBy[c.customer] || '',
          }));
        }
        return { ...prev, summary: newSummary, customer_wise: nextRows };
      });
      if (force) toast.success(`Synced from ${d.source_filename}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setAutofillStatus('error');
      if (e.response?.status === 403) toast.error(detail || 'Cannot read Schedule vs Supply for this unit');
      else if (force) toast.error(detail || 'Re-sync failed');
    } finally {
      setSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitName, period]);

  // Autofill only once the saved entry (if any) has loaded, otherwise the
  // entry fetch can land afterwards and reset the pre-filled figures to 0.
  useEffect(() => {
    if (ctx.loading) return;
    touchedRef.current = {};
    sync(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitName, period, ctx.loading]);

  // Live freight maths — self-pickup customers leave the denominator only.
  // (Their sale still counts in Total Dispatched and Adherence — only the
  // freight ratio excludes them, otherwise freight % is artificially low.)
  const cw = ctx.data.customer_wise || [];
  const freightableDispatched = cw.reduce(
    (a, r) => a + (r.ex_works ? 0 : Number(r.dispatched_value) || 0),
    0,
  );
  const exworksDispatched = cw.reduce(
    (a, r) => a + (r.ex_works ? Number(r.dispatched_value) || 0 : 0),
    0,
  );
  const freightCost = Number(s.total_freight_cost) || 0;
  const freightPct =
    freightableDispatched > 0 ? +(freightCost / freightableDispatched * 100).toFixed(2) : 0;

  // Keep summary + per-row adherence in lock-step with dispatched/schedule, so the
  // form matches the PPT (which derives the same numbers). Guarded to avoid loops.
  useEffect(() => {
    const rows = ctx.data.customer_wise || [];
    let rowsChanged = false;
    const newRows = rows.map((r) => {
      const sv = Number(r.schedule_value) || 0;
      const dv = Number(r.dispatched_value) || 0;
      const ad = sv > 0 ? Number(((dv / sv) * 100).toFixed(1)) : 0;
      if (r.adherence_pct !== ad) { rowsChanged = true; return { ...r, adherence_pct: ad }; }
      return r;
    });
    const schedSum = rows.reduce((a, r) => a + (Number(r.schedule_value) || 0), 0);
    const dispSum = rows.reduce((a, r) => a + (Number(r.dispatched_value) || 0), 0);
    // The export's own month % is authoritative when it is the source; the
    // customer table (ERP .xls, ₹-based) then only drives the per-row %.
    const derived = source?.ops
      ? (Number(s.schedule_adherence_pct) || 0)
      : schedSum > 0 ? Number(((dispSum / schedSum) * 100).toFixed(1)) : (Number(s.schedule_adherence_pct) || 0);
    const summaryChanged = (Number(s.schedule_adherence_pct) || 0) !== derived;
    if (rowsChanged || summaryChanged) {
      ctx.setData({ ...ctx.data, customer_wise: rowsChanged ? newRows : rows, summary: { ...s, schedule_adherence_pct: derived } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.data.customer_wise, s.schedule_adherence_pct, source?.ops]);

  return (
    <FormShell
      title="Business Performance & Sales"
      subtitle="Sales, freight, premium freight and customer-wise adherence"
      status={ctx.status}
      save={ctx.save}
      submit={ctx.submit}
      saving={ctx.saving}
      submitting={ctx.submitting}
      canEdit={canEdit}
      locked={locked}
    >
      <SyncBanner
        status={autofillStatus}
        source={source}
        unitName={currentUnit?.name}
        period={period}
        syncing={syncing}
        onResync={() => sync(true)}
        canEdit={canEdit && !locked}
      />

      <Card title="Summary">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MoneyField
            label="Total Dispatched Value (₹)"
            value={s.total_dispatched_value}
            onChange={(v) => set('total_dispatched_value', v)}
            disabled={!canEdit || locked}
            data-testid="field-total-dispatched"
          />
          <MoneyField
            label="Total Freight Cost (₹)"
            value={s.total_freight_cost}
            onChange={(v) => set('total_freight_cost', v)}
            disabled={!canEdit || locked}
          />
          <NumberField
            label="Premium Freight Instances"
            value={s.premium_freight_instances}
            onChange={(v) => set('premium_freight_instances', v)}
            disabled={!canEdit || locked}
          />
          <MoneyField
            label="Premium Freight Cost (₹)"
            value={s.premium_freight_cost}
            onChange={(v) => set('premium_freight_cost', v)}
            disabled={!canEdit || locked}
          />
          <ReadOnlyField
            label="Schedule Adherence % (auto)"
            value={s.schedule_adherence_pct != null && s.schedule_adherence_pct !== '' ? `${s.schedule_adherence_pct}%` : '—'}
            hint={source?.ops
              ? 'Month % from the Operations KPI export (dispatched ÷ scheduled pieces)'
              : 'Dispatched ÷ Schedule (from customer table)'}
          />
        </div>

        {/* Computed freight metrics — read-only; self-pickup excluded from the denominator */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="aampl-label">Freightable Dispatched Value (₹)</label>
            <div
              className="aampl-input bg-slate-50 cursor-default"
              data-testid="field-freightable-dispatched"
            >
              {formatCurrency(freightableDispatched)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Excludes self-pickup ({formatCurrency(exworksDispatched)})
            </div>
          </div>
          <div>
            <label className="aampl-label">Freight % (excl. self-pickup)</label>
            <div
              className="aampl-input bg-slate-50 cursor-default"
              data-testid="field-freight-pct"
            >
              {freightPct}%
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Freight cost ÷ freightable dispatched
            </div>
          </div>
        </div>

        <div className="mt-3">
          <TextAreaField
            label="Notes"
            value={s.notes}
            onChange={(v) => set('notes', v)}
            disabled={!canEdit || locked}
          />
        </div>
      </Card>

      <Card
        title="Customer-wise Schedule Adherence"
        action={
          <span className="text-xs text-slate-500">
            Pre-filled from Schedule vs Supply • Self-pickup defaults from Customer Master • Remarks manual
          </span>
        }
      >
        <RepeatTable
          rows={ctx.data.customer_wise || []}
          columns={CW_COLS}
          onChange={(rows) => {
            markTouched('customer_wise');
            ctx.setData({ ...ctx.data, customer_wise: rows });
          }}
          addLabel="Add Customer"
          defaultRow={{
            customer_name: '',
            schedule_value: 0,
            dispatched_value: 0,
            adherence_pct: 0,
            ex_works: false,
            remarks: '',
          }}
        />
      </Card>
    </FormShell>
  );
}

function SyncBanner({ status, source, unitName, period, syncing, onResync, canEdit }) {
  if (status === 'loading') {
    return (
      <div className="mb-4 px-3 py-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600" data-testid="sync-banner-loading">
        <RefreshCw size={12} className="inline mr-1 animate-spin" />
        Checking Schedule vs Supply for {unitName} / {period}…
      </div>
    );
  }
  if (status === 'none') {
    return (
      <div className="mb-4 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-900 flex items-center justify-between" data-testid="sync-banner-none">
        <span>
          <AlertTriangle size={12} className="inline mr-1" />
          No Schedule vs Supply report for {unitName} / {period}. Upload one in{' '}
          <strong>Supply Chain → Schedule vs Supply</strong>.
        </span>
        <button className="text-xs underline" onClick={onResync} disabled={syncing}>
          Retry
        </button>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="mb-4 px-3 py-2 rounded border border-red-300 bg-red-50 text-xs text-red-900" data-testid="sync-banner-error">
        Could not load Schedule vs Supply for {unitName} / {period}.
      </div>
    );
  }
  if (source?.ops) {
    const p = source.pieces || {};
    const n = (v) => (v === null || v === undefined ? '–' : Number(v).toLocaleString('en-IN'));
    return (
      <div className="mb-4 px-3 py-2 rounded border border-sky-300 bg-sky-50 text-xs text-sky-900" data-testid="sync-banner-ops">
        <div className="flex items-center justify-between">
          <span>
            <CheckCircle2 size={12} className="inline mr-1" />
            Fetched from the <strong>{source.filename}</strong> for {unitName} / {period}
            {source.uploaded_at && <> (uploaded {new Date(source.uploaded_at).toLocaleString()})</>}. Values are editable.
          </span>
          {canEdit && (
            <button className="text-xs underline disabled:opacity-50" onClick={onResync} disabled={syncing} data-testid="btn-resync">
              <RefreshCw size={11} className={`inline mr-0.5 ${syncing ? 'animate-spin' : ''}`} />
              Re-sync
            </button>
          )}
        </div>
        <div className="mt-1 text-[11px] text-sky-800">
          Export figures (pieces): schedule {n(p.monthly_schedule_pcs)} • to date {n(p.schedule_to_date_pcs)} • dispatched {n(p.dispatched_pcs)} • pending {n(p.pending_pcs)} • {n(p.parts)} parts • month {p.pct_month ?? '–'}% / to-date {p.pct_to_date ?? '–'}%.
          {source.xls
            ? <>Customer-wise rows below come from the ERP upload <strong>{source.xls}</strong>. Freight is not in either source — enter it manually.</>
            : <>The export has no per-customer split and no freight figures — the customer table stays manual (or upload the ERP .xls in <strong>Supply Chain → Schedule vs Supply</strong>), and freight is entered by hand.</>}
        </div>
      </div>
    );
  }
  if (source) {
    return (
      <div className="mb-4 px-3 py-2 rounded border border-emerald-300 bg-emerald-50 text-xs text-emerald-900 flex items-center justify-between" data-testid="sync-banner-ok">
        <span>
          <CheckCircle2 size={12} className="inline mr-1" />
          Synced from Schedule vs Supply — <strong>{source.filename}</strong>
          {source.uploaded_at && <> ({new Date(source.uploaded_at).toLocaleString()})</>} for{' '}
          {unitName} / {period}. Values are editable.
        </span>
        {canEdit && (
          <button
            className="text-xs underline disabled:opacity-50"
            onClick={onResync}
            disabled={syncing}
            data-testid="btn-resync"
          >
            <RefreshCw size={11} className={`inline mr-0.5 ${syncing ? 'animate-spin' : ''}`} />
            Re-sync
          </button>
        )}
      </div>
    );
  }
  return null;
}
